import { DragEvent, useCallback, useMemo, useRef, useState } from 'react'

type UseImageDropZoneProps = {
  onDropFiles: (files: File[]) => void
  disabled: boolean
}

// テキスト選択のドラッグなど、ファイルを伴わないドラッグではドロップ領域を反応させない。
const containsFiles = (event: DragEvent<HTMLElement>) =>
  event.dataTransfer.types.includes('Files')

/**
 * 要素全体をファイルのドロップ領域にするフック。
 * 返した dropZoneProps を対象要素に展開し、isDraggingOver でドロップ可能な表示に切り替える。
 */
export const useImageDropZone = ({
  onDropFiles,
  disabled,
}: UseImageDropZoneProps) => {
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  // 子要素をまたぐたびに dragenter と dragleave が対で発生するため、
  // 入れ子の深さを数えて 0 になったときだけ解除する（表示のちらつき防止）。
  const dragDepth = useRef(0)

  const handleDragEnter = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled || !containsFiles(event)) return
      event.preventDefault()
      dragDepth.current += 1
      setIsDraggingOver(true)
    },
    [disabled]
  )

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled || !containsFiles(event)) return
      // preventDefault しないとブラウザ既定の処理になり drop が発火しない。
      event.preventDefault()
      event.dataTransfer.dropEffect = 'copy'
    },
    [disabled]
  )

  const handleDragLeave = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled || !containsFiles(event)) return
      dragDepth.current = Math.max(0, dragDepth.current - 1)
      if (dragDepth.current === 0) {
        setIsDraggingOver(false)
      }
    },
    [disabled]
  )

  const handleDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled || !containsFiles(event)) return
      // preventDefault しないとブラウザが画像を別タブで開いてしまう。
      event.preventDefault()
      dragDepth.current = 0
      setIsDraggingOver(false)
      onDropFiles(Array.from(event.dataTransfer.files))
    },
    [disabled, onDropFiles]
  )

  const dropZoneProps = useMemo(
    () => ({
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    }),
    [handleDragEnter, handleDragOver, handleDragLeave, handleDrop]
  )

  return { isDraggingOver, dropZoneProps }
}
